import React, { useEffect, useState } from 'react';
import { LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import {
  claimPendingWorkOSOAuth,
  finishWorkOSOAuthAttempt,
  getWorkOSOAuthSessionStorage,
  WorkOSOAuthClaimResult,
  WorkOSOAuthFailureReason,
} from '../navigation/workosOAuth';
import { BRAND_MARK_SRC, PRODUCT_NAME } from '../brand';
import AuthFlowShell from './AuthFlowShell';
import './WorkOSOAuthCompletionPage.css';

type CompletionState =
  | { kind: 'connecting' }
  | { kind: 'error'; reason: WorkOSOAuthFailureReason };

interface WorkOSOAuthCompletionPageProps {
  redirect?: (url: string) => void;
  returnToApp?: () => void;
}

const inFlightCompletions = new Map<string, Promise<string>>();
const defaultRedirect = (url: string): void => window.location.replace(url);
const defaultReturnToApp = (): void => window.location.assign('/mcp');

const getFailureCopy = (reason: WorkOSOAuthFailureReason): string => {
  switch (reason) {
    case 'account_changed':
      return 'This authorization was started for a different account. Return to ChatGPT or Codex and start linking again with the account you want to connect.';
    case 'storage_unavailable':
      return 'This browser could not securely retain the authorization request in this tab. Enable session storage, then restart linking from ChatGPT or Codex.';
    case 'invalid':
      return 'This authorization request is invalid. Return to ChatGPT or Codex and start linking again.';
    case 'interrupted':
      return 'The previous linking attempt was interrupted and cannot be retried safely. Return to ChatGPT or Codex and start linking again.';
    case 'completion_failed':
      return 'We could not complete this authorization, and it is not safe to retry the same request. Return to ChatGPT or Codex and start linking again.';
    case 'missing':
    default:
      return 'No active authorization request was found in this tab. Return to ChatGPT or Codex and start linking again.';
  }
};

export const validateWorkOSCompletionRedirect = (value: unknown): string => {
  if (typeof value !== 'string' || !value.trim()) {
    throw new Error('WorkOS completion did not return a redirect');
  }
  const redirect = new URL(value);
  if (redirect.protocol !== 'https:' || redirect.username || redirect.password) {
    throw new Error('WorkOS completion returned an unsafe redirect');
  }
  return redirect.href;
};

const getOrStartCompletion = (
  claim: Exclude<WorkOSOAuthClaimResult, { kind: 'unavailable' }>,
): Promise<string> | null => {
  const existing = inFlightCompletions.get(claim.attemptId);
  if (existing) return existing;
  if (claim.kind !== 'claimed') return null;

  const request = apiService
    .completeWorkOSOAuth(claim.externalAuthId)
    .then((response) => validateWorkOSCompletionRedirect(response.redirect_uri));
  inFlightCompletions.set(claim.attemptId, request);
  void request.then(
    () => inFlightCompletions.delete(claim.attemptId),
    () => inFlightCompletions.delete(claim.attemptId),
  );
  return request;
};

const WorkOSOAuthCompletionPage: React.FC<WorkOSOAuthCompletionPageProps> = ({
  redirect = defaultRedirect,
  returnToApp = defaultReturnToApp,
}) => {
  const { currentUser, logout } = useAuth();
  const [state, setState] = useState<CompletionState>({ kind: 'connecting' });
  const currentUserId = currentUser?.uid || '';

  useEffect(() => {
    let active = true;
    const storage = getWorkOSOAuthSessionStorage();
    const claim = claimPendingWorkOSOAuth(currentUserId, storage);
    if (claim.kind === 'unavailable') {
      setState({ kind: 'error', reason: claim.reason });
      return () => {
        active = false;
      };
    }

    const completion = getOrStartCompletion(claim);
    if (!completion) {
      finishWorkOSOAuthAttempt(claim.attemptId, storage, 'interrupted');
      setState({ kind: 'error', reason: 'interrupted' });
      return () => {
        active = false;
      };
    }

    completion.then(
      (redirectUri) => {
        if (!active) return;
        finishWorkOSOAuthAttempt(claim.attemptId, storage);
        redirect(redirectUri);
      },
      () => {
        if (!active) return;
        finishWorkOSOAuthAttempt(claim.attemptId, storage, 'completion_failed');
        setState({ kind: 'error', reason: 'completion_failed' });
      },
    );

    return () => {
      // The completion call is intentionally not aborted or retried: after the
      // server receives the one-shot handoff, its outcome may be ambiguous.
      active = false;
    };
  }, [currentUserId, redirect]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      // The surrounding authentication boundary reports logout failures.
    }
  };

  const isConnecting = state.kind === 'connecting';
  return (
    <AuthFlowShell busy={isConnecting}>
      <div className="workos-oauth-card auth-flow-card auth-flow-loading-card">
        <div className="auth-flow-brand" aria-label={PRODUCT_NAME}>
          <img className="auth-flow-brand-mark" src={BRAND_MARK_SRC} alt="" aria-hidden="true" />
          <span>{PRODUCT_NAME}</span>
        </div>

        {isConnecting ? (
          <div aria-live="polite">
            <div className="auth-flow-spinner" aria-hidden="true" />
            <p className="auth-flow-eyebrow">Secure account linking</p>
            <h1 className="auth-flow-title">Connecting ChatGPT and Codex</h1>
            <p className="auth-flow-copy">
              Keep this tab open while we return you to the application that requested access.
              This connection can use the VideoVector tools available to your account.
            </p>
          </div>
        ) : (
          <div role="alert" aria-live="assertive">
            <p className="auth-flow-eyebrow">Authorization needs attention</p>
            <h1 className="auth-flow-title">Restart account linking</h1>
            <p className="auth-flow-copy">{getFailureCopy(state.reason)}</p>
            <div className="auth-flow-actions">
              <button type="button" className="auth-flow-primary" onClick={returnToApp}>
                Return to VideoVector
              </button>
              <button type="button" className="auth-flow-secondary" onClick={() => void handleLogout()}>
                <LogOut size={16} aria-hidden="true" />
                Sign out
              </button>
            </div>
          </div>
        )}
      </div>
    </AuthFlowShell>
  );
};

export default WorkOSOAuthCompletionPage;
